import {
    Box,
    Button,
    Chip,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    Switch,
    TextField,
    Typography
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { Formik } from "formik";
import * as Yup from "yup";
import React from "react";
import Call from "../../../model/database/Call";
import CustomDialog from "../../common/CustomDialog";
import CircularLoading from "../../common/CircularLoading";

interface CallEvaluationModalProps {
    open: boolean;
    call?: Call;
    isAdmin: boolean;
    loggedUserVatNumber?: string;
    handleClose: () => void;
    handleSubmit: (id: string, body: EvaluationBody) => Promise<void>;
}

export interface EvaluationBody {
    partitaIva?: string;
    cittaSedi?: string[];
    numeroDipendenti?: number;
    codiciAteco?: string[];
    fatturatoUltimi3Bilanci?: number[];
    isStartup?: boolean;
    dataCostituzione?: string;
}

const hasNonEmpty = (arr: string[]) => arr.some(v => (v ?? '').trim() !== '');

const validationSchema = Yup.object().shape({
    partitaIva: Yup.string().required('La Partita IVA è obbligatoria'),
    numeroDipendenti: Yup.number()
        .min(0, 'Il valore minimo è 0')
        .required('Il numero di dipendenti è obbligatorio'),
    cittaSedi: Yup.array().of(Yup.string())
        .test('at-least-one-city', 'Almeno una sede è obbligatoria', val => !!val && hasNonEmpty(val as string[])),
    codiciAteco: Yup.array().of(Yup.string())
        .test('at-least-one-ateco', 'Almeno un codice ATECO è obbligatorio', val => !!val && hasNonEmpty(val as string[])),
    fatturatoUltimi3Bilanci: Yup.array().of(Yup.number().min(0))
        .min(1, 'Almeno un anno di fatturato è obbligatorio'),
    dataCostituzione: Yup.string().required('La data di costituzione è obbligatoria'),
});

const CallEvaluationModal = ({ open, call, isAdmin, loggedUserVatNumber, handleClose, handleSubmit }: CallEvaluationModalProps) => {
    const callId: string = call?.product?.id ?? '';

    return (
        <Formik
            enableReinitialize
            initialValues={{
                partitaIva: isAdmin ? '' : (loggedUserVatNumber ?? ''),
                cittaSedi: [] as string[],
                numeroDipendenti: 0,
                codiciAteco: [] as string[],
                fatturatoUltimi3Bilanci: [] as number[],
                isStartup: false,
                dataCostituzione: '',
                atecoInput: '',
                cittaInput: '',
                fatturatoInput: '',
            }}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting }) => {
                const body: EvaluationBody = {
                    partitaIva: values.partitaIva,
                    cittaSedi: values.cittaSedi,
                    numeroDipendenti: values.numeroDipendenti,
                    codiciAteco: values.codiciAteco.filter(v => v.trim() !== ''),
                    fatturatoUltimi3Bilanci: values.fatturatoUltimi3Bilanci,
                    isStartup: values.isStartup,
                    dataCostituzione: values.dataCostituzione,
                };
                await handleSubmit(callId, body);
                setSubmitting(false);
            }}
        >
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit: formikSubmit, isSubmitting, setFieldValue }) => (
                <CustomDialog
                    open={open}
                    handleClose={handleClose}
                    title="Valutazione di ammissibilità"
                    content={
                        <form onSubmit={formikSubmit}>
                            <Grid container spacing={2} sx={{ mt: 0.5 }}>

                                {/* Riga compatta: Partita IVA | Data costituzione | Num. dipendenti | Startup */}
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        label="Partita IVA *"
                                        name="partitaIva"
                                        value={values.partitaIva}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        disabled={isSubmitting || !isAdmin}
                                        inputProps={{ readOnly: !isAdmin }}
                                        error={Boolean(touched.partitaIva && errors.partitaIva)}
                                        helperText={touched.partitaIva && errors.partitaIva}
                                        variant="outlined"
                                        size="small"
                                    />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        label="Data di costituzione *"
                                        name="dataCostituzione"
                                        type="date"
                                        value={values.dataCostituzione}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        disabled={isSubmitting}
                                        InputLabelProps={{ shrink: true }}
                                        error={Boolean(touched.dataCostituzione && errors.dataCostituzione)}
                                        helperText={touched.dataCostituzione && errors.dataCostituzione}
                                        variant="outlined"
                                        size="small"
                                    />
                                </Grid>

                                <Grid item xs={12} md={2}>
                                    <TextField
                                        fullWidth
                                        label="N. dipendenti *"
                                        name="numeroDipendenti"
                                        type="number"
                                        inputProps={{ min: 0 }}
                                        value={values.numeroDipendenti}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        disabled={isSubmitting}
                                        error={Boolean(touched.numeroDipendenti && errors.numeroDipendenti)}
                                        helperText={touched.numeroDipendenti && errors.numeroDipendenti}
                                        variant="outlined"
                                        size="small"
                                    />
                                </Grid>

                                <Grid item xs={12} md={1} display="flex" alignItems="center">
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={values.isStartup}
                                                onChange={e => setFieldValue('isStartup', e.target.checked)}
                                                disabled={isSubmitting}
                                                color="secondary"
                                                size="small"
                                            />
                                        }
                                        label={<Typography variant="body2">Startup</Typography>}
                                        sx={{ ml: 0 }}
                                    />
                                </Grid>

                                {/* Codici ATECO — chips */}
                                <Grid item xs={12}>
                                    <Typography
                                        variant="subtitle2"
                                        color={touched.codiciAteco && errors.codiciAteco ? 'error' : 'text.primary'}
                                        sx={{ mb: 1 }}
                                    >
                                        Codici ATECO *
                                    </Typography>
                                    <Box display="flex" alignItems="center" mb={1}>
                                        <TextField
                                            label="Aggiungi codice ATECO"
                                            name="atecoInput"
                                            value={values.atecoInput}
                                            onChange={handleChange}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter' && (values.atecoInput as string).trim()) {
                                                    e.preventDefault();
                                                    setFieldValue('codiciAteco', [...values.codiciAteco, (values.atecoInput as string).trim()]);
                                                    setFieldValue('atecoInput', '');
                                                }
                                            }}
                                            disabled={isSubmitting}
                                            variant="outlined"
                                            size="small"
                                            sx={{ flex: 1 }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton size="small" onClick={() => {
                                                            if ((values.atecoInput as string).trim()) {
                                                                setFieldValue('codiciAteco', [...values.codiciAteco, (values.atecoInput as string).trim()]);
                                                                setFieldValue('atecoInput', '');
                                                            }
                                                        }}>
                                                            <Add />
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Box>
                                    <Box display="flex" flexWrap="wrap" gap={0.5} mb={0.5}>
                                        {values.codiciAteco.map((code, idx) => (
                                            <Chip
                                                key={idx}
                                                label={code}
                                                size="small"
                                                onDelete={() => setFieldValue('codiciAteco', values.codiciAteco.filter((_, i) => i !== idx))}
                                                disabled={isSubmitting}
                                            />
                                        ))}
                                    </Box>
                                    {touched.codiciAteco && errors.codiciAteco && typeof errors.codiciAteco === 'string' && (
                                        <Typography variant="caption" color="error">{errors.codiciAteco}</Typography>
                                    )}
                                </Grid>

                                {/* Sedi (città) — chips */}
                                <Grid item xs={12}>
                                    <Typography
                                        variant="subtitle2"
                                        color={touched.cittaSedi && errors.cittaSedi ? 'error' : 'text.primary'}
                                        sx={{ mb: 1 }}
                                    >
                                        Sedi (città) *
                                    </Typography>
                                    <Box display="flex" alignItems="center" mb={1}>
                                        <TextField
                                            label="Aggiungi città"
                                            name="cittaInput"
                                            value={values.cittaInput}
                                            onChange={handleChange}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter' && (values.cittaInput as string).trim()) {
                                                    e.preventDefault();
                                                    setFieldValue('cittaSedi', [...values.cittaSedi.filter(v => v.trim() !== ''), (values.cittaInput as string).trim()]);
                                                    setFieldValue('cittaInput', '');
                                                }
                                            }}
                                            disabled={isSubmitting}
                                            variant="outlined"
                                            size="small"
                                            sx={{ flex: 1 }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton size="small" onClick={() => {
                                                            if ((values.cittaInput as string).trim()) {
                                                                setFieldValue('cittaSedi', [...values.cittaSedi.filter(v => v.trim() !== ''), (values.cittaInput as string).trim()]);
                                                                setFieldValue('cittaInput', '');
                                                            }
                                                        }}>
                                                            <Add />
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Box>
                                    <Box display="flex" flexWrap="wrap" gap={0.5} mb={0.5}>
                                        {values.cittaSedi.filter(v => v.trim() !== '').map((city, idx) => (
                                            <Chip
                                                key={idx}
                                                label={city}
                                                size="small"
                                                onDelete={() => setFieldValue('cittaSedi', values.cittaSedi.filter(v => v !== city))}
                                                disabled={isSubmitting}
                                            />
                                        ))}
                                    </Box>
                                    {touched.cittaSedi && errors.cittaSedi && typeof errors.cittaSedi === 'string' && (
                                        <Typography variant="caption" color="error">{errors.cittaSedi}</Typography>
                                    )}
                                </Grid>

                                {/* Fatturato ultimi 3 bilanci — chips */}
                                <Grid item xs={12}>
                                    <Typography
                                        variant="subtitle2"
                                        color={touched.fatturatoUltimi3Bilanci && errors.fatturatoUltimi3Bilanci ? 'error' : 'text.primary'}
                                        sx={{ mb: 1 }}
                                    >
                                        Fatturato ultimi bilanci (max 3) *
                                    </Typography>
                                    {values.fatturatoUltimi3Bilanci.length < 3 && (
                                        <Box display="flex" alignItems="center" mb={1}>
                                            <TextField
                                                label="Aggiungi fatturato (€)"
                                                name="fatturatoInput"
                                                type="number"
                                                inputProps={{ min: 0 }}
                                                value={values.fatturatoInput}
                                                onChange={handleChange}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter') {
                                                        const val = parseFloat((values.fatturatoInput as string));
                                                        if (!isNaN(val) && values.fatturatoUltimi3Bilanci.length < 3) {
                                                            e.preventDefault();
                                                            setFieldValue('fatturatoUltimi3Bilanci', [...values.fatturatoUltimi3Bilanci, val]);
                                                            setFieldValue('fatturatoInput', '');
                                                        }
                                                    }
                                                }}
                                                disabled={isSubmitting}
                                                variant="outlined"
                                                size="small"
                                                sx={{ flex: 1 }}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton size="small" onClick={() => {
                                                                const val = parseFloat((values.fatturatoInput as string));
                                                                if (!isNaN(val) && values.fatturatoUltimi3Bilanci.length < 3) {
                                                                    setFieldValue('fatturatoUltimi3Bilanci', [...values.fatturatoUltimi3Bilanci, val]);
                                                                    setFieldValue('fatturatoInput', '');
                                                                }
                                                            }}>
                                                                <Add />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                        </Box>
                                    )}
                                    <Box display="flex" flexWrap="wrap" gap={0.5} mb={0.5}>
                                        {values.fatturatoUltimi3Bilanci.map((val, idx) => (
                                            <Chip
                                                key={idx}
                                                label={`${val.toLocaleString('it-IT')} €`}
                                                size="small"
                                                onDelete={() => setFieldValue('fatturatoUltimi3Bilanci', values.fatturatoUltimi3Bilanci.filter((_, i) => i !== idx))}
                                                disabled={isSubmitting}
                                            />
                                        ))}
                                    </Box>
                                    {touched.fatturatoUltimi3Bilanci && errors.fatturatoUltimi3Bilanci && typeof errors.fatturatoUltimi3Bilanci === 'string' && (
                                        <Typography variant="caption" color="error">{errors.fatturatoUltimi3Bilanci}</Typography>
                                    )}
                                </Grid>

                            </Grid>
                        </form>
                    }
                    actions={
                        <>
                            <Button color="inherit" disabled={isSubmitting} onClick={handleClose}>
                                Annulla
                            </Button>
                            <Button variant="contained" disabled={isSubmitting} onClick={() => formikSubmit()} autoFocus>
                                Invia valutazione
                                {isSubmitting && <CircularLoading />}
                            </Button>
                        </>
                    }
                />
            )}
        </Formik>
    );
};

export default CallEvaluationModal;
